/**
 * Loads the marketing / analytics / chat integrations that are switched LIVE
 * in admin.isman.co.ke → Settings → Integrations, and counts anonymous page views.
 * Nothing loads inside the admin's live editor preview.
 */
type Live = {
  ga4?: string; gsc?: string; meta_pixel?: string; tiktok?: string; clarity?: string;
  tawk?: { p: string; w: string }; whatsapp?: { n: string; m: string };
};
type W = Window & {
  dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void; fbq?: ((...a: unknown[]) => void) & { q?: unknown[] };
  ttq?: { track: (e: string, d?: unknown) => void; page: () => void }; clarity?: unknown; Tawk_API?: unknown;
};
const w = window as W;
let live: Live = {};
const editing = new URLSearchParams(location.search).get("isman_edit") === "1";

function script(src: string, attrs: Record<string, string> = {}) {
  const s = document.createElement("script"); s.async = true; s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}
function inline(code: string) { const s = document.createElement("script"); s.text = code; document.head.appendChild(s); }

function load(l: Live) {
  live = l;
  if (l.gsc && !document.querySelector('meta[name="google-site-verification"][data-isman]')) {
    const m = document.createElement("meta"); m.name = "google-site-verification"; m.content = l.gsc; m.setAttribute("data-isman", ""); document.head.appendChild(m);
  }
  if (l.ga4) {
    script("https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(l.ga4));
    w.dataLayer = w.dataLayer || [];
    w.gtag = function () { (w.dataLayer as unknown[]).push(arguments); }; // eslint-disable-line prefer-rest-params
    w.gtag("js", new Date()); w.gtag("config", l.ga4);
  }
  if (l.meta_pixel) {
    inline(`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${l.meta_pixel}');fbq('track','PageView');`);
  }
  if (l.tiktok) {
    inline(`!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${l.tiktok}');ttq.page();}(window,document,'ttq');`);
  }
  if (l.clarity) {
    inline(`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${l.clarity}");`);
  }
  if (l.tawk) {
    w.Tawk_API = w.Tawk_API || {};
    script(`https://embed.tawk.to/${l.tawk.p}/${l.tawk.w}`, { charset: "UTF-8", crossorigin: "*" });
  } else if (l.whatsapp) {
    const a = document.createElement("a");
    a.href = `https://wa.me/${l.whatsapp.n}${l.whatsapp.m ? "?text=" + encodeURIComponent(l.whatsapp.m) : ""}`;
    a.target = "_blank"; a.rel = "noopener"; a.setAttribute("aria-label", "Chat on WhatsApp");
    a.style.cssText = "position:fixed;left:18px;bottom:18px;z-index:60;width:54px;height:54px;border-radius:50%;background:#25D366;color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 24px rgba(0,0,0,.25);font-size:28px";
    a.innerHTML = '<i class="ri-whatsapp-line"></i>';
    document.body.appendChild(a);
  }
}

let lastPath = "";
function pageView() {
  const p = location.pathname;
  if (p === lastPath) return; lastPath = p;
  try {
    fetch("/api/store/hit.php", { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ p, r: document.referrer }) }).catch(() => {});
  } catch { /* ignore */ }
  if (live.ga4 && w.gtag) w.gtag("event", "page_view", { page_path: p, page_title: document.title });
  if (live.meta_pixel && w.fbq) w.fbq("track", "PageView");
  if (live.tiktok && w.ttq) w.ttq.page();
}

/** Call after a successful order so ad platforms can measure sales. */
export function trackPurchase(ref: string, total: number, currency = "KES", items: { id: number | string; name: string; qty: number; price: number }[] = []) {
  if (live.ga4 && w.gtag) w.gtag("event", "purchase", { transaction_id: ref, value: total, currency, items: items.map((i) => ({ item_id: String(i.id), item_name: i.name, quantity: i.qty, price: i.price })) });
  if (live.meta_pixel && w.fbq) w.fbq("track", "Purchase", { value: total, currency, content_ids: items.map((i) => String(i.id)), content_type: "product" });
  if (live.tiktok && w.ttq) w.ttq.track("CompletePayment", { value: total, currency });
}
export function trackAddToCart(id: number | string, name: string, price: number, currency = "KES") {
  if (live.ga4 && w.gtag) w.gtag("event", "add_to_cart", { currency, value: price, items: [{ item_id: String(id), item_name: name, price, quantity: 1 }] });
  if (live.meta_pixel && w.fbq) w.fbq("track", "AddToCart", { value: price, currency, content_ids: [String(id)], content_type: "product" });
  if (live.tiktok && w.ttq) w.ttq.track("AddToCart", { value: price, currency });
}

export function initSiteIntegrations() {
  if (editing || window.parent !== window) return;
  fetch("/api/store/site.php", { cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { if (j?.integrations) load(j.integrations); })
    .catch(() => {})
    .finally(() => {
      pageView();
      for (const k of ["pushState", "replaceState"] as const) {
        const orig = history[k];
        history[k] = function (this: History, ...args: Parameters<typeof orig>) { const r = orig.apply(this, args); setTimeout(pageView, 50); return r; } as typeof orig;
      }
      addEventListener("popstate", () => setTimeout(pageView, 50));
    });
}
